import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { addPurchaseOrder } from "../services/purchaseService";
import { getSuppliers } from "../services/supplierService";
import { getRawMaterials } from "../services/rawMaterialService";

import "./AddPurchaseOrder.css";

const AddPurchaseOrderLayer = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [errors, setErrors] = useState({});

  /* ================= FORM ================= */
  const [form, setForm] = useState({
    reference_no: "",
    invoice_no: "",
    supplier: "",
    date: "",
    subtotal: 0,
    discount: 0,
    grand_total: 0,
    paid: 0,
    note: "",
    type: "purchase",
  });

  /* ================= ITEMS ================= */
  const [items, setItems] = useState([
    { raw_material_id: "", unit_price: "", quantity: "", total: 0 },
  ]);

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    loadSuppliers();
    loadRawMaterials();
  }, []);

  const loadSuppliers = async () => {
    const res = await getSuppliers();
    if (res?.status === "success") setSuppliers(res.data || []);
  };

  const loadRawMaterials = async () => {
    const res = await getRawMaterials();
    if (res?.status === "success") setRawMaterials(res.data || []);
  };

  /* ================= FORM CHANGE ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: "" }));
  };

  /* ================= ITEM HANDLERS ================= */
  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;

    updated[index].total =
      Number(updated[index].unit_price || 0) *
      Number(updated[index].quantity || 0);

    setItems(updated);
    calculateTotals(updated);
  };

  const addItemRow = () => {
    setItems([
      ...items,
      { raw_material_id: "", unit_price: "", quantity: "", total: 0 },
    ]);
  };

  const calculateTotals = (itemsData) => {
    const subtotal = itemsData.reduce(
      (s, i) => s + Number(i.total || 0),
      0
    );

    const grand = subtotal - Number(form.discount || 0);

    setForm((p) => ({
      ...p,
      subtotal,
      grand_total: grand,
    }));
  };

  /* ================= VALIDATION ================= */
  const validate = () => {
    const e = {};

    if (!form.reference_no) e.reference_no = "Reference number required";
    if (!form.supplier) e.supplier = "Supplier required";
    if (!form.date) e.date = "Date required";

    items.forEach((item, index) => {
      if (!item.raw_material_id)
        e[`item_material_${index}`] = "Material required";
      if (!item.unit_price || item.unit_price <= 0)
        e[`item_price_${index}`] = "Valid price required";
      if (!item.quantity || item.quantity <= 0)
        e[`item_qty_${index}`] = "Valid quantity required";
    });

    if (Number(form.paid) > Number(form.grand_total))
      e.paid = "Paid amount cannot exceed grand total";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      toast.error("Please fix form errors");
      return;
    }

    setLoading(true);

    try {
      const res = await addPurchaseOrder({
        ...form,
        items,
      });

      if (res?.status === "success") {
        toast.success("Purchase order added successfully");
        navigate("/purchase-orders");
      } else {
        toast.error(res?.message || "Failed");
      }
    } catch {
      toast.error("API Error");
    }

    setLoading(false);
  };

  return (
    <div className="container-fluid">
      <div className="card purchase-card">
        <h4 className="mb-4">Add Purchase Order</h4>

        <form onSubmit={handleSubmit} noValidate>
          {/* BASIC INFO */}
          <div className="row g-3">
            <div className="col-lg-4 col-md-6 col-12">
              <label className="form-label">PO Reference *</label>
              <input
                name="reference_no"
                value={form.reference_no}
                onChange={handleChange}
                className="form-control"
              />
              {errors.reference_no && (
                <small className="text-danger">{errors.reference_no}</small>
              )}
            </div>

            <div className="col-lg-4 col-md-6 col-12">
              <label className="form-label">Invoice No</label>
              <input
                name="invoice_no"
                value={form.invoice_no}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-lg-4 col-md-6 col-12">
              <label className="form-label">Date *</label>
              <input
                type="date"
                name="date"
                value={form.date}
                onChange={handleChange}
                className="form-control"
              />
              {errors.date && (
                <small className="text-danger">{errors.date}</small>
              )}
            </div>

            <div className="col-lg-4 col-md-6 col-12">
              <label className="form-label">Supplier *</label>
              <select
                name="supplier"
                value={form.supplier}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">Select</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              {errors.supplier && (
                <small className="text-danger">{errors.supplier}</small>
              )}
            </div>
          </div>

          {/* ITEMS */}
          <h5 className="mt-4">Items</h5>

          {items.map((i, idx) => (
            <div
              key={idx}
              className="row g-2 mb-3 purchase-item-row align-items-end"
            >
              <div className="col-lg-4 col-md-6 col-12">
                <label className="form-label">Raw Material *</label>
                <select
                  className="form-select"
                  value={i.raw_material_id}
                  onChange={(e) =>
                    handleItemChange(idx, "raw_material_id", e.target.value)
                  }
                >
                  <option value="">Select</option>
                  {rawMaterials.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
                {errors[`item_material_${idx}`] && (
                  <small className="text-danger">
                    {errors[`item_material_${idx}`]}
                  </small>
                )}
              </div>

              <div className="col-lg-2 col-md-3 col-6">
                <label className="form-label">Price *</label>
                <input
                  type="number"
                  className="form-control"
                  onChange={(e) =>
                    handleItemChange(idx, "unit_price", e.target.value)
                  }
                />
                {errors[`item_price_${idx}`] && (
                  <small className="text-danger">
                    {errors[`item_price_${idx}`]}
                  </small>
                )}
              </div>

              <div className="col-lg-2 col-md-3 col-6">
                <label className="form-label">Qty *</label>
                <input
                  type="number"
                  className="form-control"
                  onChange={(e) =>
                    handleItemChange(idx, "quantity", e.target.value)
                  }
                />
                {errors[`item_qty_${idx}`] && (
                  <small className="text-danger">
                    {errors[`item_qty_${idx}`]}
                  </small>
                )}
              </div>

              <div className="col-lg-2 col-md-6 col-12">
                <label className="form-label">Total</label>
                <input className="form-control" value={i.total} disabled />
              </div>
            </div>
          ))}

          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={addItemRow}
          >
            + Add Item
          </button>

          {/* TOTALS */}
          <div className="row g-3 mt-4 totals-box">
            <div className="col-lg-3 col-md-6 col-12">
              <label className="form-label">Subtotal</label>
              <input className="form-control" value={form.subtotal} disabled />
            </div>

            <div className="col-lg-3 col-md-6 col-12">
              <label className="form-label">Discount</label>
              <input
                type="number"
                name="discount"
                value={form.discount}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-lg-3 col-md-6 col-12">
              <label className="form-label">Grand Total</label>
              <input
                className="form-control"
                value={form.grand_total}
                disabled
              />
            </div>

            <div className="col-lg-3 col-md-6 col-12">
              <label className="form-label">Paid</label>
              <input
                type="number"
                name="paid"
                value={form.paid}
                onChange={handleChange}
                className="form-control"
              />
              {errors.paid && (
                <small className="text-danger">{errors.paid}</small>
              )}
            </div>
          </div>

          {/* ACTIONS */}
          <div className="mt-4 d-flex flex-wrap gap-3">
            <button className="btn btn-primary" disabled={loading}>
              {loading ? "Saving..." : "Submit"}
            </button>
            <Link to="/purchase-orders" className="btn btn-secondary">
              Back
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPurchaseOrderLayer;
